import { ContentConverter } from '@/utils/contentConverter';

export interface CustomHttpConnection {
  id: string;
  settings: {
    endpoint_url: string;
    http_method: 'POST' | 'PUT';
    headers: Record<string, string>;
    payload_mapping: Record<string, string>;
    format?: 'markdown' | 'html' | 'json';
  };
}

export interface CustomHttpPost {
  title: string;
  content: string;
  tags?: string[];
  excerpt?: string;
}

export interface PublishingProgress {
  connectionId: string;
  status: 'in_progress' | 'success' | 'error';
  message: string;
  externalUrl?: string;
}

export class CustomHttpPublishingService {
  async publishToCustomHttp(
    connection: CustomHttpConnection,
    post: CustomHttpPost,
    onProgress?: (progress: PublishingProgress) => void
  ): Promise<void> {
    const progressCallback = onProgress || (() => {});
    
    try {
      progressCallback({
        connectionId: connection.id,
        status: 'in_progress',
        message: 'Converting content format...'
      });

      // Convert content based on connection format setting
      const targetFormat = connection.settings.format || 'html';
      const convertedContent = await ContentConverter.prepareForPublishing(
        post.content,
        targetFormat,
        {
          title: post.title,
          tags: post.tags,
          excerpt: post.excerpt
        }
      );

      progressCallback({
        connectionId: connection.id,
        status: 'in_progress',
        message: 'Preparing payload...'
      });

      // Build the payload using the mapping template
      const payload = this.buildPayload(connection.settings.payload_mapping, {
        title: post.title,
        content: convertedContent,
        tags: post.tags?.join(', ') || '',
        excerpt: post.excerpt || ''
      });

      progressCallback({
        connectionId: connection.id,
        status: 'in_progress',
        message: 'Publishing to endpoint...'
      });

      // Make the HTTP request
      const response = await fetch(connection.settings.endpoint_url, {
        method: connection.settings.http_method,
        headers: {
          'Content-Type': 'application/json',
          ...connection.settings.headers
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const responseData = await response.json().catch(() => ({}));
      
      progressCallback({
        connectionId: connection.id,
        status: 'success',
        message: 'Successfully published to custom endpoint',
        externalUrl: responseData.url || connection.settings.endpoint_url
      });

    } catch (error) {
      progressCallback({
        connectionId: connection.id,
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }

  private buildPayload(mapping: Record<string, string>, data: Record<string, string>): any {
    const payload: any = {};
    
    for (const [key, template] of Object.entries(mapping)) {
      // Replace template variables like {{title}}, {{content}}, etc.
      let value = template;
      for (const [dataKey, dataValue] of Object.entries(data)) {
        value = value.replace(new RegExp(`{{${dataKey}}}`, 'g'), dataValue);
      }
      
      // Try to parse as JSON if it looks like JSON, otherwise use as string
      try {
        if (value.startsWith('{') || value.startsWith('[')) {
          payload[key] = JSON.parse(value);
        } else {
          payload[key] = value;
        }
      } catch {
        payload[key] = value;
      }
    }
    
    return payload;
  }
}

export const customHttpPublishingService = new CustomHttpPublishingService();
